<template>
  <div class="adjust-task-manage">
    <basic-container>
      <!-- 顶部：价盘下拉 + 搜索 + 新增 -->
      <div class="toolbar">
        <el-form inline size="small" @submit.native.prevent>
          <el-form-item label="价盘">
            <el-select
              v-model="priceBookId"
              filterable
              clearable
              placeholder="请选择价盘（必选）"
              style="width: 280px"
              @change="handlePriceBookChange"
            >
              <el-option
                v-for="pb in priceBookOptions"
                :key="pb.id"
                :label="pbLabel(pb)"
                :value="pb.id"
              >
                <span style="float: left">{{ pb.name }}</span>
                <span style="float: right; color: #909399; font-size: 12px">
                  {{ projectTypeText(pb.projectType) }}
                </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="toolbar-right">
          <el-button
            type="primary"
            icon="el-icon-plus"
            :disabled="!priceBookId"
            @click="handleCreate"
          >新增调价</el-button>
        </div>
      </div>

      <!-- 表格 -->
      <avue-crud
        ref="crud"
        :option="tableOption"
        :data="tableData"
        :page="page"
        :table-loading="tableLoading"
        @on-load="handleList"
        @search-change="handleSearch"
        @search-reset="handleReset"
        @refresh-change="handleList"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      >
        <template slot="adjustTypeName" slot-scope="scope">
          <el-tag
            v-if="scope.row.batchType === 'CANCEL'"
            type="danger"
            size="mini"
          >作废申请</el-tag>
          <span v-else>{{ scope.row.adjustTypeName || '-' }}</span>
        </template>
        <template slot="adjustValue" slot-scope="scope">
          <el-tag
            v-if="scope.row.batchType === 'CANCEL'"
            type="danger"
            size="mini"
          >作废申请</el-tag>
          <span v-else>{{ buildAdjustParamText(scope.row) }}</span>
        </template>
        <template slot="displayStatus" slot-scope="scope">
          <el-tag :type="displayStatusType(scope.row.displayStatus)" size="small">
            {{ scope.row.displayStatus || "-" }}
          </el-tag>
        </template>
        <template slot="menu" slot-scope="scope">
          <el-button type="text" icon="el-icon-view" size="small" @click="handleView(scope.row)">
            查看
          </el-button>
          <el-button
            v-if="hasEffectiveItem(scope.row)"
            type="text"
            icon="el-icon-close"
            size="small"
            style="color: #f56c6c"
            @click="handleCancel(scope.row)"
          >
            作废
          </el-button>
        </template>
      </avue-crud>
    </basic-container>

    <!-- 批量作废弹窗 -->
    <el-dialog
      title="作废调价任务"
      :visible.sync="cancelDialogVisible"
      width="720px"
      append-to-body
      :close-on-click-modal="false"
    >
      <div class="cancel-tip">
        以下为当前生效中的明细，提交作废审批后，审批期间任务保持生效，全部通过后任务作废恢复原价。可取消勾选保留部分明细：
      </div>
      <el-table
        ref="cancelTable"
        :data="cancelEffectiveItems"
        border
        size="small"
        max-height="360"
        @selection-change="handleCancelSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="merchantName" label="商户名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="merchantPhone" label="电话" width="120" />
        <el-table-column label="型号" min-width="120">
          <template slot-scope="scope">{{ scope.row.goodsFilterName || '-' }}</template>
        </el-table-column>
        <el-table-column label="原价(元/㎡)" prop="originalPrice" width="110" align="center" />
        <el-table-column label="调整后(元/㎡)" width="120" align="center">
          <template slot-scope="scope">
            <span style="color: #f56c6c; font-weight: 600">{{ scope.row.adjustedPrice }}</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelDialogVisible = false">取消</el-button>
        <el-button
          type="danger"
          :loading="cancelLoading"
          :disabled="!cancelSelectedTaskIds.length"
          @click="handleBatchCancel"
        >提交作废审批</el-button>
      </span>
    </el-dialog>

    <!-- 新增/查看 弹窗 -->
    <PriceAdjustFormDialog
      :key="formDialogKey"
      :visible.sync="formDialogVisible"
      :mode="formDialogMode"
      :price-book="currentPriceBook"
      :task-row="currentTask"
      @submit-success="handleList"
    />
  </div>
</template>

<script>
import { formatterTime } from "@/util/util";
import {
  priceAdjustTableOption,
  displayStatusTagType,
  approvalStatusTextMap,
} from "@/const/work/waterProof/priceAdjustOption";
import {
  findPriceAdjustTaskList,
  cancelPriceAdjust,
} from "@/api/work/waterProof/priceAdjust";
import { findPriceBookPage } from "@/api/work/waterProof/merchantPrice";
import { projectTypeDic } from "@/const/work/waterProof/merchantPriceOption";
import PriceAdjustFormDialog from "../components/PriceAdjustFormDialog.vue";

export default {
  name: "priceAdjustManage",
  components: { PriceAdjustFormDialog },
  data() {
    return {
      priceBookId: "",
      priceBookOptions: [],
      tableOption: priceAdjustTableOption,
      tableData: [],
      tableLoading: false,
      page: { total: 0, currentPage: 1, pageSize: 10 },
      searchForm: {},
      formDialogVisible: false,
      formDialogMode: "create", // create | view
      currentPriceBook: null,
      currentTask: null,
      // 批量作废
      cancelDialogVisible: false,
      cancelLoading: false,
      cancelEffectiveItems: [],
      cancelSelectedTaskIds: [],
      // 弹窗 key：每次打开自增，强制 PriceAdjustFormDialog 完全重挂载，
      // 彻底规避 element-ui 内部 renderCell 闭包捕获的 $scopedSlots 残留问题
      formDialogKey: 0,
    };
  },
  created() {
    this.loadPriceBookOptions();
  },
  methods: {
    formatTime: formatterTime,
    displayTime(v) {
      if (v == null || v === "") return "";
      if (typeof v === "string" && !/^\d+$/.test(v)) return v;
      return this.formatTime(v);
    },
    parseData(res) {
      return typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
    },
    projectTypeText(t) {
      const item = projectTypeDic.find((d) => d.value === t);
      return item ? item.label : t || "";
    },
    displayStatusType(text) {
      return displayStatusTagType[text] || "info";
    },
    approvalStatusText(status) {
      return approvalStatusTextMap[status] || status || "-";
    },
    approvalStatusType(status) {
      if (status === "PENDING") return "warning";
      if (status === "REJECTED") return "danger";
      if (status === "APPROVED") return "success";
      if (status === "CANCELLED") return "info";
      return "info";
    },
    // 调价参数展示（按 adjustType 分支）：
    // FIXED/DISCOUNT/ADD → "${adjustTypeName} ${adjustValue}"
    // FORMULA → "公式 ${formula}"（adjustValue 为 null 时不处理）
    // CANCEL 批次 → "作废申请"
    buildAdjustParamText(row) {
      if (!row) return "-";
      if (row.batchType === "CANCEL") return "作废申请";
      if (row.adjustType === "FORMULA") {
        return row.formula ? `公式 ${row.formula}` : "公式";
      }
      const value = row.adjustValue != null && row.adjustValue !== "" ? row.adjustValue : "";
      if (!value) return row.adjustTypeName || "-";
      return `${row.adjustTypeName || ""} ${value}`.trim();
    },
    // 是否存在生效中的明细（可作废）
    hasEffectiveItem(row) {
      return (row.items || []).some((i) => i.taskStatus === "EFFECTIVE");
    },
    pbLabel(pb) {
      const tags = [];
      tags.push(this.projectTypeText(pb.projectType));
      if (pb.isFallback === "YES") tags.push("兜底");
      return `${pb.name}（${tags.join("/")}）`;
    },
    // 价盘下拉全量（size=1000 拉全量；超过再考虑加 list 接口）
    loadPriceBookOptions() {
      findPriceBookPage({ page: 1, size: 1000 })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.priceBookOptions = data.rows || [];
          } else {
            this.$message.error(res.message || "价盘加载失败");
          }
        })
        .catch((err) => {
          console.log("loadPriceBookOptions error:", err);
        });
    },
    handlePriceBookChange() {
      this.searchForm = {};
      this.page.currentPage = 1;
      this.handleList();
    },
    buildParams() {
      return {
        priceBookId: this.priceBookId,
        page: this.page.currentPage,
        size: this.page.pageSize,
      };
    },
    handleList() {
      if (!this.priceBookId) {
        this.tableData = [];
        this.page.total = 0;
        return;
      }
      this.tableLoading = true;
      findPriceAdjustTaskList(this.buildParams())
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.tableData = data.rows || [];
            this.page.total = data.total || 0;
          } else {
            this.$message.error(res.message || "加载失败");
          }
        })
        .catch((err) => {
          console.log("handleList error:", err);
          this.$message.error("加载异常");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    handleSearch() {
      this.page.currentPage = 1;
      this.handleList();
    },
    handleReset() {
      this.searchForm = {};
      this.page.currentPage = 1;
      this.handleList();
    },
    handlePageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    handleSizeChange(size) {
      this.page.pageSize = size;
      this.page.currentPage = 1;
      this.handleList();
    },
    // 新增
    handleCreate() {
      if (!this.priceBookId) {
        this.$message.warning("请先选择价盘");
        return;
      }
      this.currentPriceBook = this.priceBookOptions.find((p) => p.id === this.priceBookId) || null;
      this.currentTask = null;
      this.formDialogMode = "create";
      this.formDialogKey += 1;
      this.formDialogVisible = true;
    },
    // 查看
    handleView(row) {
      this.currentPriceBook = this.priceBookOptions.find((p) => p.id === this.priceBookId) || null;
      this.currentTask = row;
      this.formDialogMode = "view";
      this.formDialogKey += 1;
      this.formDialogVisible = true;
    },
    // 作废：批次维度下按生效中的明细行逐条作废
    handleCancel(row) {
      const effectiveItems = (row.items || []).filter(
        (i) => i.taskStatus === "EFFECTIVE" && i.taskId
      );
      if (!effectiveItems.length) {
        this.$message.warning("当前没有可作废的生效明细");
        return;
      }
      if (effectiveItems.length === 1) {
        this.confirmCancelItem(effectiveItems[0]);
      } else {
        this.cancelEffectiveItems = effectiveItems;
        this.cancelSelectedTaskIds = effectiveItems.map((i) => i.taskId);
        this.cancelDialogVisible = true;
      }
    },
    handleCancelSelectionChange(rows) {
      this.cancelSelectedTaskIds = (rows || []).map((r) => r.taskId);
    },
    confirmCancelItem(item) {
      this.$confirm(
        `确定提交作废「${item.merchantName}」的调价任务吗？审批期间任务保持生效，全部通过后任务作废恢复原价。`,
        "提示",
        { confirmButtonText: "确定", cancelButtonText: "取消", type: "warning" }
      )
        .then(() => {
          this.cancelLoading = true;
          cancelPriceAdjust({ taskIds: [item.taskId] })
            .then((res) => {
              if (res.code === "200") {
                const data = this.parseData(res);
                this.$message.success(data.message || "已提交审批，全部通过后任务作废恢复原价");
                this.handleList();
              } else {
                this.$message.error(res.message || "作废提交失败");
              }
            })
            .catch((err) => {
              console.log("handleCancel error:", err);
              this.$message.error("作废提交异常");
            })
            .finally(() => {
              this.cancelLoading = false;
            });
        })
        .catch(() => {});
    },
    // 批量作废（多条生效明细，一次请求传 taskIds 数组）
    handleBatchCancel() {
      const ids = this.cancelSelectedTaskIds;
      if (!ids.length) {
        this.$message.warning("请选择要作废的明细");
        return;
      }
      this.cancelLoading = true;
      cancelPriceAdjust({ taskIds: ids })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.$message.success(data.message || "已提交审批，全部通过后任务作废恢复原价");
          } else {
            this.$message.error(res.message || "作废提交失败");
          }
        })
        .catch((err) => {
          console.log("handleBatchCancel error:", err);
          this.$message.error("作废提交异常");
        })
        .finally(() => {
          this.cancelLoading = false;
          this.cancelDialogVisible = false;
          this.handleList();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.adjust-task-manage {
  .toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .toolbar-right {
    flex-shrink: 0;
  }
  .cancel-tip {
    margin-bottom: 10px;
    font-size: 13px;
    color: #f56c6c;
  }
}
</style>
